import React from 'react';
import {Button, ButtonProps, Tooltip} from '@mui/material';
import {CloseRounded, EditOutlined} from '@mui/icons-material';
import {DateRange} from './DateRange.class';
import DateRangePopover, {DateRangePopoverProps} from './DateRangePopover';
import UIUtils from "../../../utils";
import {DateRangeFormProps} from "./DateRangeForm";

export interface DateRangeButtonProps extends DateRangeFormProps {
    /** Custom props forwarded to the MUI Button (excluding onClick / disabled) */
    buttonProps?: Omit<ButtonProps, 'onClick' | 'disabled'>;
    /** Text shown when value is empty / unresolved. Defaults to "Select Date Range". */
    placeholder?: string;
    /** Custom popover positioning props forwarded to the DateRangePopover (optional) */
    popoverProps?: Pick<DateRangePopoverProps, 'anchorOrigin' | 'transformOrigin'>;
}

const DateRangeButton: React.FC<DateRangeButtonProps> = (props) => {
    const {buttonProps, placeholder: _placeholder, popoverProps, ...formProps} = props;

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = React.useMemo(() => Boolean(anchorEl), [anchorEl]);
    const placeholder = React.useMemo(() => _placeholder ?? 'Select Date Range', [_placeholder]);

    /**
     * Opens the popover by setting the anchor element to the button that was clicked.
     */
    const openPopover = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    /**
     * Closes the popover by clearing the anchor element.
     */
    const closePopover = React.useCallback(() => {
        setAnchorEl(null);
    }, []);

    const label = React.useMemo(() => {
        if (!formProps.value) return undefined;
        return DateRange.from(formProps.value, formProps.inputMode).display() ?? undefined;
    }, [formProps.inputMode, formProps.value]);

    const hasValue = React.useMemo(() => !!label, [label]);

    /**
     * Clears the current value by invoking the onCommitted callback with undefined,
     * and stops the click event from propagating to prevent reopening the popover immediately after clearing.
     */
    const onCleared = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        formProps.onCommitted?.(undefined);
    }, [formProps]);

    return (
        <>
            <Button
                size="small"
                variant={"outlined"}
                color="secondary"
                startIcon={hasValue ? <EditOutlined/> : undefined}
                {...buttonProps}
                sx={(theme) => ({
                    minWidth: 'min(200px, 100%)',
                    ...UIUtils.invokeSx(buttonProps?.sx, theme) as any,
                })}
                disabled={formProps.disabled}
                onClick={openPopover}
                endIcon={
                    hasValue &&
                    <Tooltip title="Clear Date Range" placement="top">
                        <CloseRounded
                            fontSize="small"
                            onClick={onCleared}
                            sx={{
                                color: 'background.paper',
                                width: 20,
                                height: 20,
                                padding: '2px',
                                backgroundColor: theme => `rgba(${theme.vars.palette.text.primaryChannel} / 0.26)`,
                                borderRadius: '50%',
                                '&:hover': {
                                    backgroundColor: theme => `rgba(${theme.vars.palette.text.primaryChannel} / 0.4)`,
                                },
                            }}
                        />
                    </Tooltip>
                }
            >
                {label ?? placeholder}
            </Button>
            <DateRangePopover
                {...formProps}
                {...popoverProps}
                open={open}
                anchorEl={anchorEl}
                onClose={closePopover}
            />
        </>
    );
};

export default DateRangeButton;
